import type { SearchLocation } from '~/modules/search/type/api';
import {
    getLocationDisplayName,
    getLocationRegionName,
} from '~/modules/professionals-category/util/location-breadcrumb';

interface LocationAwareMetaInput {
    category: string | undefined;
    appliedLocation: SearchLocation | undefined;
    /** True on a category-less location hub (e.g. /professionals/location-austin). */
    isHub: boolean;
}

interface LocationAwareMeta {
    titleFallback: string;
    descriptionFallback: string;
}

interface ProfessionalDirectoryDisplayTitleInput {
    /** CMS directory header title for non-location pages. */
    directoryHeaderTitle: string;
    /** Category pretty title, e.g. "Architects". */
    categoryTitle: string;
    /** Most specific place name from the resolved location. */
    appliedLocationName: string;
    /** True only on path-based `location-{slug}` pages. */
    isLocationPage: boolean;
    /** True on a category-less location hub (e.g. /professionals/location-austin). */
    locationHub: boolean;
}

/**
 * Visible page title for professional directory pages. Location pages use the same
 * "{category} in {place}" pattern as the footer SEO mesh and hub pages read
 * "Professionals in {place}".
 */
export const buildProfessionalDirectoryDisplayTitle = ({
    directoryHeaderTitle,
    categoryTitle,
    appliedLocationName,
    isLocationPage,
    locationHub,
}: ProfessionalDirectoryDisplayTitleInput): string => {
    if (!isLocationPage || !appliedLocationName) {
        return directoryHeaderTitle;
    }

    if (locationHub) {
        return `Professionals in ${appliedLocationName}`;
    }

    return `${categoryTitle} in ${appliedLocationName}`;
};

/**
 * Location-aware title/description fallbacks so each category × location and hub
 * page has a distinct, place-specific title/description. These are only fallbacks
 * — CMS localisation still takes precedence in the route's `meta`.
 */
export const buildLocationAwareMeta = ({
    category,
    appliedLocation,
    isHub,
}: LocationAwareMetaInput): LocationAwareMeta => {
    const locationName = getLocationDisplayName(appliedLocation);
    const locationRegion = getLocationRegionName(appliedLocation);

    if (isHub && locationName) {
        return {
            titleFallback: `Professionals in ${locationRegion}`,
            descriptionFallback: `Find architects, builders, designers and more professionals in ${locationRegion}. ArchiPro connects you with leading local professionals for your next project.`,
        };
    }

    if (locationName && category) {
        return {
            titleFallback: `Best ${category} in ${locationRegion}`,
            descriptionFallback: `Find the best ${category} in ${locationRegion}. ArchiPro profiles and connects you with leading local professionals, quickly and easily.`,
        };
    }

    return {
        titleFallback: category
            ? `Find the Best ${category} Near You`
            : 'Page Not Found',
        descriptionFallback: `Find the best ${category} for your next project. ArchiPro profiles and connects you with leading professionals, quickly and easily.`,
    };
};
